import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Inbox,
  Building2,
  ScrollText,
  Stamp,
  BookOpen,
  ClipboardCheck,
  Scale,
  Archive,
  FileHeart,
  Share2,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Landmark,
  HelpCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { ThemeToggle } from '../ui/theme-toggle';
import { clearSession, getUser } from '../../lib/instituteAuth';
import { getRoleLabel } from '../../lib/roleLabels';

const MENU_SECTIONS = [
  {
    title: 'Inicio',
    items: [
      { to: '/instituto/escritorio', icon: LayoutDashboard, label: 'Escritorio', capability: 'institute.dashboard.view', featureFlag: 'institute_notarial_management' },
      { to: '/instituto/bandeja', icon: Inbox, label: 'Mi bandeja', capability: 'institute.inbox.read', featureFlag: 'institute_notarial_management' },
    ],
  },
  {
    title: 'Control notarial',
    items: [
      { to: '/instituto/padron', icon: Building2, label: 'Padrón', capability: 'institute.notaries.read', featureFlag: 'institute_notary_registry' },
      { to: '/instituto/patentes', icon: ScrollText, label: 'Aspirantes y patentes', capability: 'institute.patents.read', featureFlag: 'institute_patents' },
      { to: '/instituto/sellos-firmas', icon: Stamp, label: 'Sellos / firmas', capability: 'institute.registry.read', featureFlag: 'institute_seals_signatures' },
      { to: '/instituto/libros-protocolos', icon: BookOpen, label: 'Libros', capability: 'institute.protocol_books.read', featureFlag: 'institute_protocol_control' },
    ],
  },
  {
    title: 'Supervisión',
    items: [
      { to: '/instituto/inspecciones', icon: ClipboardCheck, label: 'Inspecciones', capability: 'institute.inspections.read', featureFlag: 'institute_inspections' },
      {
        to: '/instituto/quejas-procedimientos',
        icon: Scale,
        label: 'Quejas',
        capabilitiesAny: [
          'institute.complaints.read',
          'institute.complaints.intake',
          'institute.complaints.preliminary_investigation',
          'institute.proceedings.initiate',
          'institute.proceedings.substantiate',
          'institute.proceedings.review_resolution',
          'institute.proceedings.issue_resolution',
        ],
        featureFlag: 'institute_complaints_proceedings',
      },
    ],
  },
  {
    title: 'Archivo y servicios',
    items: [
      { to: '/instituto/archivo', icon: Archive, label: 'Archivo', capability: 'institute.archive.read', featureFlag: 'institute_notarial_archive' },
      { to: '/instituto/testamentos', icon: FileHeart, label: 'Testamentos', capability: 'institute.testaments.read', featureFlag: 'institute_testaments' },
    ],
  },
  {
    title: 'Intercambio',
    items: [
      { to: '/instituto/expedientes-compartidos', icon: Share2, label: 'Expedientes compartidos', capability: 'institute.shared_matters.read', featureFlag: 'institute_shared_matters' },
    ],
  },
  {
    title: 'Gestión',
    items: [
      { to: '/instituto/estadistica', icon: BarChart3, label: 'Estadística', capability: 'institute.statistics.view', featureFlag: 'institute_notarial_statistics' },
      { to: '/instituto/configuracion', icon: Settings, label: 'Configuración', capability: 'institute.settings.manage', featureFlag: 'institute_notarial_management' },
    ],
  },
];

export { MENU_SECTIONS };

function resolveFeatures(user) {
  if (!user) return null;
  if (user.features && typeof user.features === 'object') return user.features;
  if (Array.isArray(user.feature_flags)) {
    return Object.fromEntries(user.feature_flags.map((f) => [f, true]));
  }
  if (user.entitlements?.features) return user.entitlements.features;
  return null;
}

function resolveCapabilities(user) {
  if (!user) return null;
  if (Array.isArray(user.capabilities)) return new Set(user.capabilities);
  if (Array.isArray(user.perms)) return new Set(user.perms);
  return null;
}

function itemAllowed(item, features, capabilities) {
  if (features) {
    const parentOn = features.institute_notarial_management === true;
    if ('institute_notarial_management' in features && !parentOn) {
      return false;
    }
    if (item.featureFlag && item.featureFlag in features) {
      if (features[item.featureFlag] !== true && !parentOn) return false;
      if (features[item.featureFlag] === false) return false;
    }
  }
  if (capabilities) {
    if (Array.isArray(item.capabilitiesAny) && item.capabilitiesAny.length) {
      if (!item.capabilitiesAny.some((c) => capabilities.has(c))) return false;
    } else if (item.capability && !capabilities.has(item.capability)) {
      return false;
    }
  }
  return true;
}

const PAGE_TITLES = {
  '/instituto/escritorio': 'Escritorio',
  '/instituto/bandeja': 'Mi bandeja',
  '/instituto/padron': 'Padrón notarial',
  '/instituto/patentes': 'Aspirantes y patentes',
  '/instituto/sellos-firmas': 'Sellos, firmas y convenios',
  '/instituto/libros-protocolos': 'Libros y protocolos',
  '/instituto/inspecciones': 'Inspecciones',
  '/instituto/quejas-procedimientos': 'Quejas y procedimientos',
  '/instituto/archivo': 'Archivo y certificaciones',
  '/instituto/testamentos': 'Testamentos',
  '/instituto/expedientes-compartidos': 'Expedientes compartidos',
  '/instituto/estadistica': 'Estadística notarial',
  '/instituto/configuracion': 'Configuración',
};

export default function InstituteLayout({ children }) {
  const user = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const sections = useMemo(() => {
    const features = resolveFeatures(user);
    const capabilities = resolveCapabilities(user);
    return MENU_SECTIONS.map((sec) => ({
      ...sec,
      items: sec.items.filter((item) => itemAllowed(item, features, capabilities)),
    })).filter((sec) => sec.items.length > 0);
  }, [user]);

  const title = useMemo(() => {
    const path = location.pathname;
    if (PAGE_TITLES[path]) return PAGE_TITLES[path];
    if (path.startsWith('/instituto/patentes/')) return 'Detalle de patente';
    return 'Instituto — Gestión Notarial';
  }, [location.pathname]);

  const handleLogout = () => {
    clearSession();
    navigate('/instituto/login', { replace: true });
  };

  const nameParts = String(user?.full_name || user?.email || 'IN')
    .split(/\s+/)
    .filter(Boolean)
    .filter((p) => !/^(lic\.?|licda\.?|dr\.?|dra\.?|ing\.?|arq\.?|mtro\.?|mtra\.?|c\.)$/i.test(p));
  const initials = (nameParts.length ? nameParts : ['I', 'N'])
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

  const Sidebar = (
    <aside className="flex flex-col h-full bg-[#061a17] text-teal-50">
      <div className="px-4 py-4 border-b border-teal-950/80">
        <Link to="/instituto/escritorio" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-950 border border-teal-800/50 shadow-sm group-hover:bg-teal-900 transition-colors">
            <Landmark className="w-5 h-5 text-amber-300" />
          </span>
          <span className="min-w-0">
            <span className="block font-manrope font-extrabold text-sm leading-tight truncate">Instituto</span>
            <span className="block text-[10px] text-teal-300/60 font-medium truncate">Gestión Notarial · Chiapas</span>
          </span>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-4" aria-label="Menú Instituto">
        {sections.map((section) => (
          <div key={section.title}>
            <p className="px-3 mb-1 text-[10px] font-extrabold uppercase tracking-wider text-teal-500/70">
              {section.title}
            </p>
            <ul className="space-y-0.5">
              {section.items.map((item) => {
                const active = location.pathname === item.to
                  || (item.to !== '/instituto/escritorio' && location.pathname.startsWith(item.to));
                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                        active
                          ? 'bg-[#0d3a32] text-white font-semibold border border-teal-800/40'
                          : 'text-teal-100/80 hover:bg-teal-950/70 hover:text-white border border-transparent'
                      }`}
                    >
                      <item.icon className={`w-4 h-4 shrink-0 ${active ? 'text-amber-300' : 'text-teal-400/55'}`} />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="p-3 border-t border-teal-950/80 space-y-2">
        <div className="flex items-start gap-2 rounded-lg bg-black/25 px-2.5 py-2 text-[10px] text-teal-200/70 leading-snug">
          <HelpCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-teal-400/70" />
          <p>Solo datos con fundamento y permiso. El intercambio con notarías es controlado y no incluye información PLD.</p>
        </div>
        <div className="flex items-center gap-2 px-1">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-teal-950 text-[11px] font-bold border border-teal-800/40">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold truncate">{user?.full_name || user?.email || 'Usuario'}</p>
            <p className="text-[10px] text-teal-400/60 truncate">{getRoleLabel(user?.role)}</p>
          </div>
          <ThemeToggle className="text-teal-300/80 hover:bg-teal-950 rounded-md p-1.5" />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-teal-100/85 hover:text-white hover:bg-teal-950/80 h-9"
          onClick={handleLogout}
          data-testid="institute-logout"
        >
          <LogOut className="w-3.5 h-3.5 mr-2" />
          Cerrar sesión
        </Button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex">
      <div className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-30">
        {Sidebar}
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/50"
            aria-label="Cerrar menú"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-72 max-w-[85vw] h-full shadow-xl">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 lg:pl-64 min-w-0 flex flex-col min-h-screen">
        <header className="sticky top-0 z-20 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur">
          <div className="px-4 sm:px-6 h-14 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden h-9 w-9 p-0"
              onClick={() => setOpen(true)}
              aria-label="Abrir menú"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <div className="min-w-0 flex-1">
              <h1 className="font-manrope font-bold text-sm sm:text-base truncate">{title}</h1>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate hidden sm:block">
                Dirección de Notarías · Supervisión e intercambio controlado
              </p>
            </div>
            <ThemeToggle className="lg:hidden text-slate-600 dark:text-slate-300 rounded-lg p-2" />
          </div>
        </header>
        <main className="flex-1 px-4 sm:px-6 py-5 sm:py-6 animate-in fade-in-50 duration-200">
          {children}
        </main>
      </div>
    </div>
  );
}
